import type { Period, TimetableAPIResponse, Week } from "src/types";
import { Tab, Tabs } from "../tabs/Tabs";
import { Avatar } from "../Avatar";
import useSWR from "preact-swr";
import { useEffect, useState } from "preact/hooks";
import Loading from "../Loading";
import { cx } from "src/html/util";
import { getCurrentWeek } from "src/util";
import { Printer } from "lucide-preact";

async function loadData() {
  const res = await fetch("/api/timetable");
  const text = await res.text();
  const data = JSON.parse(text);
  return data as TimetableAPIResponse;
}

export default function TimetablePage() {
  const [initialLoad, setInitialLoad] = useState(true);
  const { error, data, mutate } = useSWR("timetable", loadData, {
    revalidateOnMount: false,
  });
  useEffect(() => {
    if (initialLoad && !data) {
      mutate(undefined, true);
      setInitialLoad(false);
    }
  }, [initialLoad]);

  if (error) {
    return (
      <div class="error">
        <p>An error occurred :(</p>
        <p class="msg">{error.message}</p>
      </div>
    );
  }
  if (!data) {
    return <Loading />;
  }
  return (
    <div class="timetable-section">
      <Tabs qs="student">
        {data?.map((data) => {
          const weekA = data.timetable.find((w) => w.id === "week_a");
          const weekB = data.timetable.find((w) => w.id === "week_b");
          return (
            <Tab
              id={data.id.toString()}
              label={
                <Avatar
                  img={data.student.img}
                  name={data.student.name}
                  form={data.student.form}
                />
              }
            >
              <Tabs qs="week">
                {data.timetable.map((h) => (
                  <Tab id={h.id} label={h.name}>
                    <Calendar week={h} />
                  </Tab>
                ))}
                {weekA && weekB ? (
                  <Tab id="print" label="Print">
                    <PrintCalendar
                      studentName={data.student.name}
                      form={data.student.form}
                      weekA={weekA}
                      weekB={weekB}
                    />
                  </Tab>
                ) : null}
              </Tabs>
            </Tab>
          );
        })}
      </Tabs>
    </div>
  );
}

function Calendar({ week }: { week: Week }) {
  // Table:
  // Days along the top
  // Each cell is a period.
  // So the data is in the wrong shape.
  // we have an array of days which have an array of periods.
  // but we want to iterate periods for each day.
  const maxPeriods = week.days.reduce((acc, day) => {
    return Math.max(acc, day.length);
  }, 1); // 1 so we have a minimum of 1 row
  const now = new Date();
  const today = now.getDay();
  let days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  if (week.id === "current") {
    const { startDate: mondayDate } = getCurrentWeek();
    const monday = new Date(mondayDate + "T12:00:00");
    days = days.map((x, i) => {
      const date = new Date(
        monday.getTime() + i * 86400_000
      ).toLocaleDateString();
      return x + " (" + date + ")";
    });
  }
  return (
    <div class="tt-wrap">
      <div class={cx("timetable", "today-" + today, "week-" + week.id)}>
        {days.map((label) => (
          <h3>{label}</h3>
        ))}
        {Array.from({ length: maxPeriods }).map((_, periodIndex) => {
          return (
            <>
              {week.days.map((day, j) => {
                // if the day has the period add it, otherwise add an empty cell
                // class is day- (j+1) because days start on sunday in JS, and monday here.
                const data = day[periodIndex];
                const empty = !data && day.length > 0;
                return (
                  <div class={cx("item", "day-" + (j + 1), { empty })}>
                    {data ? (
                      <PeriodBlock period={data} />
                    ) : (
                      <p class="subject">Nothing ;)</p>
                    )}
                  </div>
                );
              })}
            </>
          );
        })}
      </div>
    </div>
  );
}

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function periodsEqual(a?: Period, b?: Period): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.subject === b.subject &&
    a.group === b.group &&
    a.location === b.location &&
    a.timing === b.timing
  );
}

function PrintCalendar({
  studentName,
  form,
  weekA,
  weekB,
}: {
  studentName: string;
  form: string;
  weekA: Week;
  weekB: Week;
}) {
  const maxPeriods = Math.max(
    1,
    ...weekA.days.map((d) => d.length),
    ...weekB.days.map((d) => d.length)
  );

  return (
    <div class="print-timetable">
      <div class="print-toolbar no-print">
        <p>
          Shared periods shown once. Differences are labelled Week A / Week B.
        </p>
        <button type="button" class="print-btn" onClick={() => window.print()}>
          <Printer size={18} /> Print
        </button>
      </div>
      <div class="print-sheet">
        <div class="print-heading">
          <h2>
            {studentName}
            {form ? <span class="print-form"> ({form})</span> : null}
          </h2>
          <p class="print-sub">Week A / Week B timetable</p>
        </div>
        <div class="timetable print-grid">
          {DAY_LABELS.map((label) => (
            <h3>{label}</h3>
          ))}
          {Array.from({ length: maxPeriods }).map((_, periodIndex) =>
            DAY_LABELS.map((_, dayIndex) => {
              const a = weekA.days[dayIndex]?.[periodIndex];
              const b = weekB.days[dayIndex]?.[periodIndex];
              const empty = !a && !b;
              const differs = !periodsEqual(a, b);

              return (
                <div
                  class={cx("item", "day-" + (dayIndex + 1), {
                    empty,
                    differs,
                  })}
                >
                  {empty ? (
                    <p class="subject">—</p>
                  ) : differs ? (
                    <div class="ab-stack">
                      {a ? (
                        <div class="ab-variant">
                          <span class="ab-tag">A</span>
                          <PeriodBlock period={a} />
                        </div>
                      ) : null}
                      {b ? (
                        <div class="ab-variant">
                          <span class="ab-tag">B</span>
                          <PeriodBlock period={b} />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <PeriodBlock period={a ?? b!} />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function PeriodBlock({ period }: { period: Period }) {
  return (
    <>
      <p
        class="subject"
        dangerouslySetInnerHTML={{ __html: period.subject }}
      ></p>
      <div class="detail">
        <p class="group">{period.group}</p>
        {period.location ? <p class="location">{period.location}</p> : null}
        <p class="timing">{period.timing.split("-")[0]}</p>
      </div>
    </>
  );
}
